/**
 * Pool-level Autoscaling tab (post-δ-redesign).
 *
 *   - Capacity per replica (operator-set SCU contribution)
 *   - min/max replica caps (the only operator control surface for
 *     scaling intent under the δ design — autoscaler is the sole
 *     writer of replicas_desired)
 *   - Cost transparency: shows the smallest billable step implied by
 *     capacity_per_replica × max_price_per_hour
 *   - Pool-scoped schedules (min/max overlays per cron window)
 *
 * Removed in v0.0.122:
 *   - SLO override editor (SLO is service-level only now)
 *   - Up/Down dynamics overrides (service-level only)
 *   - Manual desired pin (POST /services/:id/desired returns 410 Gone)
 *   - "Link to service" button (no overrides to drop anymore)
 *
 * See `docs/proposals/pool-failover-and-autoscale-redesign.md` §5.2.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Gauge, Plus, Save, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import type { Pool, Schedule, Service } from "@/api/types";
import { CronBuilder, describeCron } from "@/components/CronBuilder";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";

interface Props {
  pool: Pool;
  service: Service;
}

export function PoolAutoscaling({ pool, service: _service }: Props) {
  const queryClient = useQueryClient();

  const [minReplicas, setMinReplicas] = useState<number>(pool.min_replicas);
  const [maxReplicas, setMaxReplicas] = useState<number>(pool.max_replicas);
  const [maxPrice, setMaxPrice] = useState<number>(pool.max_price_per_hour);
  const [capacity, setCapacity] = useState<number>(
    pool.capacity_per_replica ?? 1.0,
  );

  useEffect(() => {
    setMinReplicas(pool.min_replicas);
    setMaxReplicas(pool.max_replicas);
    setMaxPrice(pool.max_price_per_hour);
    setCapacity(pool.capacity_per_replica ?? 1.0);
  }, [pool.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Two-way min<=max follow: bumping min above max drags max up, and
  // dragging max below min drags min down. Same pattern as
  // ResourceShapePicker's GPU-count linkage.
  const onMinChange = (next: number) => {
    const n = Math.max(0, Math.floor(next));
    setMinReplicas(n);
    if (n > maxReplicas) setMaxReplicas(n);
  };
  const onMaxChange = (next: number) => {
    const n = Math.max(0, Math.floor(next));
    setMaxReplicas(n);
    if (n < minReplicas) setMinReplicas(n);
  };

  const minHourlyCost = useMemo(
    () => minReplicas * maxPrice,
    [minReplicas, maxPrice],
  );
  const smallestUnitCost = useMemo(
    () => capacity * maxPrice,
    [capacity, maxPrice],
  );

  const save = useMutation({
    mutationFn: () =>
      api.patch<Pool>(`/pools/${pool.id}`, {
        min_replicas: minReplicas,
        max_replicas: maxReplicas,
        max_price_per_hour: maxPrice,
        capacity_per_replica: capacity,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pools"] });
      queryClient.invalidateQueries({ queryKey: ["pools", "all"] });
    },
  });

  return (
    <>
      <Section
        title="Pool scaling"
        icon={<Gauge />}
        actions={
          <button
            className="btn btn--primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save />
            save
          </button>
        }
      >
        <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
          SLO and up/down dynamics are now defined service-wide on the{" "}
          <strong>Service &rsaquo; Autoscaling</strong> tab; the autoscaler
          decides a service-level demand in SCU and the allocator fans it
          out across pools by priority. This pool only exposes its
          relative capacity and the min/max replica window the allocator
          must stay within.
        </p>

        <div className="form-grid" style={{ marginBottom: 16 }}>
          <label>capacity_per_replica</label>
          <div className="field">
            <input
              type="number"
              step="0.1"
              min={0.01}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value) || 0)}
              style={{ width: 120 }}
            />
            <div className="field-hint">
              Operator-defined relative capacity of one replica in this
              pool versus the service-wide SCU unit. Multiple factors
              influence the realised throughput (FLOPS, memory
              bandwidth, parallelism, batching) so we surface this as an
              operator-owned knob rather than auto-deriving from the
              accelerator name.
            </div>
          </div>
          <label>min_replicas</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={minReplicas}
              onChange={(e) => onMinChange(Number(e.target.value))}
              style={{ width: 120 }}
            />
            <div className="field-hint">
              Hard floor; allocator never drops below this even when
              demand is zero. Warm floor: min ≥ 1 keeps this pool warm
              as an instant failover target.{" "}
              {minReplicas > 0 && (
                <strong style={{ color: "var(--color-warn, #b58900)" }}>
                  Minimum continuous cost ≈ ${minHourlyCost.toFixed(2)}
                  /hour (≈ ${(minHourlyCost * 730).toFixed(0)}/month)
                  while this pool is warm.
                </strong>
              )}
            </div>
          </div>
          <label>max_replicas</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={maxReplicas}
              onChange={(e) => onMaxChange(Number(e.target.value))}
              style={{ width: 120 }}
            />
            <div className="field-hint">
              Hard ceiling. Increasing min above max (or decreasing max
              below min) drags the partner field along so the constraint
              <code> min ≤ max</code> always holds.
            </div>
          </div>
          <label>max_price_per_hour</label>
          <div className="field">
            <input
              type="number"
              step="0.01"
              min={0}
              value={maxPrice}
              onChange={(e) => setMaxPrice(Number(e.target.value))}
              style={{ width: 120 }}
            />
            <div className="field-hint">
              USD per replica · hour. Smallest scaling unit on this pool
              ≈ ${smallestUnitCost.toFixed(2)} / hour (
              <code>capacity_per_replica × max_price_per_hour</code>).
            </div>
          </div>
        </div>

        {save.isError && (
          <div className="flash" style={{ marginTop: 12 }}>
            failed to save:{" "}
            <span className="mono">{(save.error as Error).message}</span>
          </div>
        )}
      </Section>

      <PoolSchedulesSection pool={pool} />
    </>
  );
}

/** Pool-scoped schedules: each entry overlays min/max for a cron window. */
function PoolSchedulesSection({ pool }: { pool: Pool }) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["schedules"],
    queryFn: () => api.get<Schedule[]>("/schedules"),
  });
  const filtered = useMemo(
    () =>
      (list.data ?? []).filter(
        (s) => s.scope === "pool" && s.target_id === pool.id,
      ),
    [list.data, pool.id],
  );

  const [cron, setCron] = useState("0 9 * * 1,2,3,4,5");
  const [minR, setMinR] = useState<number | undefined>(undefined);
  const [maxR, setMaxR] = useState<number | undefined>(undefined);
  const [note, setNote] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.post<Schedule>("/schedules", {
        scope: "pool",
        target_id: pool.id,
        cron_expr: cron,
        min_replicas: minR,
        max_replicas: maxR,
        note: note || undefined,
        enabled: true,
        priority: 100,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });
  const del = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/schedules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const reservedHint =
    pool.reservation_id != null
      ? `This pool is backed by reservation #${pool.reservation_id}. To keep the reserved capacity hot, pin min_replicas = the reservation count.`
      : null;

  return (
    <Section
      title="Schedules"
      icon={<CalendarClock />}
      count={`${filtered.length}`}
    >
      {reservedHint && (
        <div className="flash" style={{ marginBottom: 12 }}>
          {reservedHint}
        </div>
      )}
      <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
        Each schedule overlays <code>min_replicas</code> /{" "}
        <code>max_replicas</code> on this pool for the matching cron
        window. The autoscaller still owns the desired count within
        those bounds — the schedule only shifts the bounds.
      </p>
      <div className="form-grid">
        <label>when</label>
        <div className="field">
          <CronBuilder value={cron} onChange={setCron} />
        </div>
        <label>min_replicas</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={minR ?? ""}
            onChange={(e) =>
              setMinR(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
          />
        </div>
        <label>max_replicas</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={maxR ?? ""}
            onChange={(e) =>
              setMaxR(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
          />
        </div>
        <label>note</label>
        <div className="field">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. 'always burn the reservation'"
          />
        </div>
      </div>
      <div className="toolbar" style={{ marginTop: 12 }}>
        <button className="btn btn--primary" onClick={() => create.mutate()}>
          <Plus />
          add for {pool.sky_service_name}
        </button>
      </div>

      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table className="data">
          <thead>
            <tr>
              <th style={{ textAlign: "right" }}>id</th>
              <th>When</th>
              <th>Cron</th>
              <th style={{ textAlign: "right" }}>Min</th>
              <th style={{ textAlign: "right" }}>Max</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="hoverable">
                <td className="num">{s.id}</td>
                <td>{describeCron(s.cron_expr)}</td>
                <td className="mono muted">{s.cron_expr}</td>
                <td className="num">{s.min_replicas ?? "—"}</td>
                <td className="num">{s.max_replicas ?? "—"}</td>
                <td>
                  <Pill tone={s.enabled ? "ok" : "neutral"}>
                    {s.enabled ? "yes" : "no"}
                  </Pill>
                </td>
                <td className="actions">
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() => del.mutate(s.id)}
                  >
                    <Trash2 />
                    delete
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={7} className="empty">
                  no schedules for this pool
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
